var kind          = require("enyo/kind"),
    dispatcher    = require("enyo/dispatcher"),
    utils         = require("enyo/utils"),
    pointerEvents = Boolean("PointerEvent" in window);

/**
 * A standardized way to describe the state of a pointer.
 *
 * @typedef   {Object}   enyo-canvas.PointerState
 * @property  {number}   pointerId    The unique identifier for the pointer
 * @property  {string}   pointerType  The type of pointer (mouse, touch)
 * @property  {Event}    srcEvent     The original PointerEvent, MouseEvent or
 *                                    TouchEvent fired on the DOM.
 * @property  {Event=}   srcTouch     The original Touch from the TouchEvent
 *                                    fired on the DOM for this particular
 *                                    pointer. Only applicable for TouchEvent
 *
 * @property  {boolean}  altKey    Copied from the original DOM event
 * @property  {boolean}  ctrlKey   Copied from the original DOM event
 * @property  {boolean}  metaKey   Copied from the original DOM event
 * @property  {boolean}  shiftKey  Copied from the original DOM event
 * @property  {number}   button    Copied from the original DOM event
 * @property  {number}   buttons   Copied from the original DOM event
 * @property  {string}   type      Copied from the original DOM event
 * @property  {number}   screenX   Copied from the original DOM event
 * @property  {number}   screenY   Copied from the original DOM event
 * @property  {number}   clientX   Copied from the original DOM event
 * @property  {number}   clientY   Copied from the original DOM event
 * @property  {number}   pageX     Copied from the original DOM event
 * @property  {number}   pageY     Copied from the original DOM event
 *
 * @property  {number}   canvasX   The x-coordinate of the pointer in canvas space
 * @property  {number}   canvasY   The y-coordinate of the pointer in canvas space
 *
 * @property  {number}   dx        The change in `clientX` since last event
 * @property  {number}   dy        The change in `clientY` since last event
 * @property  {number}   ddx       The change in `dx` since last event
 * @property  {number}   ddy       The change in `dy` since last event
 * @property  {number}   movement  The total movement of since pointer down
 *
 * @property  {boolean}                    pointerOnCanvas  Whether the original DOM event targeted the canvas tag
 * @property  {enyo-canvas.CanvasControl}  previousTarget   The control targeted by the pointer during the previous event
 * @property  {enyo-canvas.CanvasControl}  target           The control targeted by the pointer
 * @property  {enyo-canvas.CanvasControl}  tapTarget        The control targeted during the `pointerdown` event
 * @property  {boolean}                    isTapPrevented   Whether `pointertap` event will be prevented from firing.
 * @property  {Function}                   preventTap       Prevents `pointertap` event from firing.
 */

/**
 * A standardized way to describe the state of a wheel.
 *
 * @typedef   {Object}   enyo-canvas.WheelState
 * @property  {Event}    srcEvent  The original WheelEvent fired on the DOM.
 *
 * @property  {boolean}  altKey    Copied from the original DOM event
 * @property  {boolean}  ctrlKey   Copied from the original DOM event
 * @property  {boolean}  metaKey   Copied from the original DOM event
 * @property  {boolean}  shiftKey  Copied from the original DOM event
 * @property  {number}   button    Copied from the original DOM event
 * @property  {number}   buttons   Copied from the original DOM event
 * @property  {string}   type      Copied from the original DOM event
 * @property  {number}   screenX   Copied from the original DOM event
 * @property  {number}   screenY   Copied from the original DOM event
 * @property  {number}   clientX   Copied from the original DOM event
 * @property  {number}   clientY   Copied from the original DOM event
 * @property  {number}   pageX     Copied from the original DOM event
 * @property  {number}   pageY     Copied from the original DOM event
 *
 * @property  {number}   canvasX   The x-coordinate of the wheel in canvas space
 * @property  {number}   canvasY   The y-coordinate of the wheel in canvas space
 */

/**
 * Fires when the a pointer enters the boundaries of a child of the
 * [Canvas]{@link enyo-canvas.Canvas}.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointerenter
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer starts touching the screen or a mouse button gets
 * pressed.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointerdown
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer moves.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointermove
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer leaves the boundaries of a child of the
 * [Canvas]{@link enyo-canvas.Canvas}.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointerleave
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer stops touching the screen or a mouse button stops
 * getting pressed.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointerup
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer stops touching the screen or a mouse button stops
 * getting pressed on the same child of the [Canvas]{@link enyo-canvas.Canvas}
 * as it started touching the screen or the mouse button started getting
 * pressed.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointertap
 * @type   {enyo-canvas.PointerState}
 */

/**
 * Fires when the a pointer is canceled.
 *
 * @event  enyo-canvas.EventDispatchSupport#pointercancel
 * @type   {enyo-canvas.PointerState}
 */

/**
 * [EventDispatchSupport]{@link enyo-canvas.EventDispatchSupport} captures
 * PointerEvents, MouseEvents and TouchEvents and tries to dispatch them to
 * the correct [CanvasControls]{@link enyo-canvas.CanvasControl}.
 *
 * Depending on browser support and the `usePointerEvents` flag, this mixin will
 * either listen exclusively to pointer events, or to mouse and touch events.
 *
 * @mixin enyo-canvas.EventDispatchSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-canvas.EventDispatchSupport",

    /**
     * Mostly just here to easily ignore pointer events entirely. Easier to test
     * mouse and touch events this way.
     *
     * @type     {boolean}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    usePointerEvents: true,

    /**
     * Currently tracked pointers.
     *
     * @type     {Object}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    pointers: null,

    /**
     * Currently captured pointers.
     *
     * @type     {Object}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    capturedPointers: null,

    /**
     * @type     {Object}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    eventListeners: null,

    /**
     * @type     {Array}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    eventProps: [
        "altKey",
        "ctrlKey",
        "metaKey",
        "shiftKey",
        "button",
        "buttons",
        "type"
    ],

    /**
     * @type     {Array}
     * @memberof enyo-canvas.EventDispatchSupport
     * @private
     */
    pointerProps: [
        "screenX",
        "screenY",
        "clientX",
        "clientY",
        "pageX",
        "pageY"
    ],

    /**
     * @private
     */
    handlers: {
        onwheel: "onWheel"
    },

    /**
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.pointers         = {};
            this.capturedPointers = {};
            this.eventListeners   = {
                length: 0
            };

            if (this.usePointerEvents && pointerEvents) {
                this.onPointerDown   = this.bindSafely(this.onPointerDown);
                this.onPointerMove   = this.bindSafely(this.onPointerMove);
                this.onPointerUp     = this.bindSafely(this.onPointerUp);
                this.onPointerCancel = this.bindSafely(this.onPointerCancel);
            }
            else {
                this.onMouseDown   = this.bindSafely(this.onMouseDown);
                this.onMouseMove   = this.bindSafely(this.onMouseMove);
                this.onMouseUp     = this.bindSafely(this.onMouseUp);
                this.onTouchStart  = this.bindSafely(this.onTouchStart);
                this.onTouchMove   = this.bindSafely(this.onTouchMove);
                this.onTouchEnd    = this.bindSafely(this.onTouchEnd);
                this.onTouchCancel = this.bindSafely(this.onTouchCancel);
            }
            sup.apply(this, arguments);
        };
    }),

    /**
     * Only listen for events when canvas is absoluteShowing.
     *
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    absoluteShowingChanged: kind.inherit(function(sup) {
        return function(wasAbsoluteShowing) {
            var absoluteShowing = this.absoluteShowing,
                pointers        = this.pointers,
                pointerId;

            sup.apply(this, arguments);

            // Start listening for events
            if (absoluteShowing && !wasAbsoluteShowing) {
                if (this.usePointerEvents && pointerEvents) {
                    dispatcher.listen(document, "pointerdown", this.onPointerDown);
                    dispatcher.listen(document, "pointermove", this.onPointerMove);
                    dispatcher.listen(document, "pointerup", this.onPointerUp);
                    dispatcher.listen(document, "pointercancel", this.onPointerCancel);
                }
                else {
                    dispatcher.listen(document, "mousedown", this.onMouseDown);
                    dispatcher.listen(document, "mousemove", this.onMouseMove);
                    dispatcher.listen(document, "mouseup", this.onMouseUp);
                    dispatcher.listen(document, "touchstart", this.onTouchStart);
                    dispatcher.listen(document, "touchmove", this.onTouchMove);
                    dispatcher.listen(document, "touchend", this.onTouchEnd);
                    dispatcher.listen(document, "touchcancel", this.onTouchCancel);
                }
            }

            // Stop listening for events
            else if (wasAbsoluteShowing && !absoluteShowing) {
                // Cancel and then unregister all pointers
                for (pointerId in pointers) {
                    if (pointers.hasOwnProperty(pointerId)) {
                        this.dispatchPointerCancelEvents(pointers[pointerId]);
                    }
                }

                if (this.usePointerEvents && pointerEvents) {
                    dispatcher.stopListening(document, "pointerdown", this.onPointerDown);
                    dispatcher.stopListening(document, "pointermove", this.onPointerMove);
                    dispatcher.stopListening(document, "pointerup", this.onPointerUp);
                    dispatcher.stopListening(document, "pointercancel", this.onPointerCancel);
                }
                else {
                    dispatcher.stopListening(document, "mousedown", this.onMouseDown);
                    dispatcher.stopListening(document, "mousemove", this.onMouseMove);
                    dispatcher.stopListening(document, "mouseup", this.onMouseUp);
                    dispatcher.stopListening(document, "touchstart", this.onTouchStart);
                    dispatcher.stopListening(document, "touchmove", this.onTouchMove);
                    dispatcher.stopListening(document, "touchend", this.onTouchEnd);
                    dispatcher.stopListening(document, "touchcancel", this.onTouchCancel);
                }
            }
        };
    }),

    /**
     * Cancel all pointers when screen resizes.
     *
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var pointers = this.pointers,
                pointerId;

            sup.apply(this, arguments);

            for (pointerId in pointers) {
                if (pointers.hasOwnProperty(pointerId)) {
                    this.dispatchPointerCancelEvents(pointers[pointerId]);
                }
            }
        };
    }),

    // ...........................
    // Event Listeners

    /**
     * Register a method on a control as handler for a particular event.
     *
     * @param    {enyo-canvas.CanvasControl}  control  The control that starts
     *                                                 listening
     * @param    {string}                     event    The event name
     * @param    {string}                     handler  The handler on the
     *                                                 control
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @public
     */
    addEventListener: function(control, event, handler) {
        var listeners = this.eventListeners,
            euid      = control.euid;

        // Ensure listener
        listeners[event] = listeners[event] || {};

        // Register handler
        if (!listeners[event][euid]) {
            listeners.length++;
        }
        listeners[event][euid] = handler;
    },

    /**
     * Unregister a method on a control as handler for a particular euid.
     *
     * @param    {enyo-canvas.CanvasControl}  control  The control that starts
     *                                                 listening
     * @param    {string}                     event    The event name
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @public
     */
    removeEventListener: function(control, event) {
        var listeners = this.eventListeners,
            euid      = control.euid;

        // Remove handler
        if (listeners[event] && listeners[event][euid]) {
            delete listeners[event][euid];
            listeners.length--;
        }
    },

    // ...........................
    // Pointer Capturing

    /**
     * Allow one control to capture a pointer. This way it'll receive
     * `pointermove` and `pointerup` events even when the pointer is not
     * positioned over the capturing control.
     *
     * @param    {number}                     pointerId  The pointer identifier
     * @param    {enyo-canvas.CanvasControl}  control    The capturing control
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @public
     */
    capturePointer: function(pointerId, control) {
        var captured = this.capturedPointers;

        if (!captured[pointerId]) {
            captured[pointerId] = [];
        }

        if (captured[pointerId].indexOf(control) === -1) {
            captured[pointerId].push(control);
        }
    },

    /**
     * Releases a previously captured pointer.
     *
     * @param    {number}                     pointerId  The pointer identifier
     * @param    {enyo-canvas.CanvasControl}  control    The capturing control
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @public
     */
    releasePointer: function(pointerId, control) {
        var captured = this.capturedPointers,
            index;

        if (captured[pointerId]) {
            index        = captured[pointerId].indexOf(control);
            index !== -1 && captured[pointerId].splice(index, 1);
        }
    },

    // ...........................
    // Incoming Pointer Events

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {PointerEvent}  event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onPointerDown: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizePointerEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerDownEvents(pointer);
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {PointerEvent}  event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onPointerMove: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizePointerEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerMoveEvents(pointer);
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {PointerEvent}  event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onPointerUp: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizePointerEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerUpEvents(pointer);
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {PointerEvent}  event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onPointerCancel: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizePointerEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerCancelEvents(pointer);
    },

    // ...........................
    // Incoming Mouse Events

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {MouseEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onMouseDown: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizeMouseEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerDownEvents(pointer);
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {MouseEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onMouseMove: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizeMouseEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerMoveEvents(pointer);
    },

    /**
     Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {MouseEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onMouseUp: function(event) {
        var pointer;

        if (!this.eventListeners.length) {
            return;
        }

        event   = this.normalizeMouseEvent(event);
        pointer = this.updatePointerState(event);
        this.dispatchPointerUpEvents(pointer);
    },

    // ...........................
    // Incoming Touch Events

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {TouchEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onTouchStart: function(event) {
        var pointerEvents,
            i,
            n,
            pointerEvent,
            pointer;

        if (!this.eventListeners.length) {
            return;
        }

        pointerEvents = this.normalizeTouchEvent(event);

        for (i = 0, n = pointerEvents.length; i < n; i++) {
            pointerEvent = pointerEvents[i];
            pointer      = this.updatePointerState(pointerEvent);
            this.dispatchPointerDownEvents(pointer);
        }
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {TouchEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onTouchMove: function(event) {
        var pointerEvents,
            i,
            n,
            pointerEvent,
            pointer;

        if (!this.eventListeners.length) {
            return;
        }

        pointerEvents = this.normalizeTouchEvent(event);

        for (i = 0, n = pointerEvents.length; i < n; i++) {
            pointerEvent = pointerEvents[i];
            pointer      = this.updatePointerState(pointerEvent);
            this.dispatchPointerMoveEvents(pointer);
        }
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {TouchEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onTouchEnd: function(event) {
        var pointerEvents,
            i,
            n,
            pointerEvent,
            pointer;

        if (!this.eventListeners.length) {
            return;
        }

        pointerEvents = this.normalizeTouchEvent(event);

        for (i = 0, n = pointerEvents.length; i < n; i++) {
            pointerEvent = pointerEvents[i];
            pointer      = this.updatePointerState(pointerEvent);
            this.dispatchPointerUpEvents(pointer);
        }
    },

    /**
     * Normalize event data and dispatch events to children.
     *
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {TouchEvent}    event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onTouchCancel: function(event) {
        var pointerEvents,
            i,
            n,
            pointerEvent,
            pointer;

        if (!this.eventListeners.length) {
            return;
        }

        pointerEvents = this.normalizeTouchEvent(event);

        for (i = 0, n = pointerEvents.length; i < n; i++) {
            pointerEvent = pointerEvents[i];
            pointer      = this.updatePointerState(pointerEvent);
            this.dispatchPointerCancelEvents(pointer);
        }
    },

    // ...........................
    // Incoming Wheel Events

    /**
     * Because events are bound on the document, they are not dispatched if
     * canvas is not absoluteShowing.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    onWheel: function(sender, event) {
        if (!this.absoluteShowing || !this.eventListeners.length) {
            return;
        }

        this.dispatchWheelEvent(this.normalizeWheelEvent(event));
    },

    // ...........................
    // Outgoing Events

    /**
     * Dispatch events to canvas controls when a pointer is down.
     *
     * @fires    enyo-canvas.EventDispatchSupport#pointerenter
     * @fires    enyo-canvas.EventDispatchSupport#pointerdown
     * @fires    enyo-canvas.EventDispatchSupport#pointerleave
     * @param    {enyo-canvas.PointerState}  pointer  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    dispatchPointerDownEvents: function(pointer) {
        var pointerId        = pointer.pointerId,
            previousTarget   = pointer.previousTarget,
            target           = pointer.target,
            capturers        = this.capturedPointers[pointerId] || [],
            targetIsCapturer = false,
            listeners        = this.eventListeners,
            handlers,
            handler,
            i,
            n,
            capturer;

        // Fire `pointerenter` if pointer just entered a new control
        if (target && target !== previousTarget) {
            handlers = listeners.pointerenter || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerdown` on capturers
        if (capturers) {
            for (i = 0, n = capturers.length; i < n; i++) {
                capturer = capturers[i];
                handlers = listeners.pointerdown || {};
                handler  = handlers[capturer.euid] || null;
                handler  && capturer[handler](pointer);

                if (capturer === target) {
                    targetIsCapturer = true;
                }
            }
        }

        // Fire `pointerdown` on target if it isn't a capturer
        if (target && !targetIsCapturer) {
            handlers = listeners.pointerdown || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerleave` if pointer just left a control
        if (previousTarget && target !== previousTarget) {
            handlers = listeners.pointerleave || {};
            handler  = handlers[previousTarget.euid] || null;
            handler  && previousTarget[handler](pointer);
        }
    },

    /**
     * Dispatch events to canvas controls when a pointer has moved.
     *
     * @fires    enyo-canvas.EventDispatchSupport#pointerenter
     * @fires    enyo-canvas.EventDispatchSupport#pointermove
     * @fires    enyo-canvas.EventDispatchSupport#pointerleave
     * @param    {enyo-canvas.PointerState}  pointer  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    dispatchPointerMoveEvents: function(pointer) {
        var pointerId        = pointer.pointerId,
            previousTarget   = pointer.previousTarget,
            target           = pointer.target,
            capturers        = this.capturedPointers[pointerId] || [],
            targetIsCapturer = false,
            listeners        = this.eventListeners,
            handlers,
            handler,
            i,
            n,
            capturer;

        // Fire `pointerenter` if pointer just entered a new control
        if (target && target !== previousTarget) {
            handlers = listeners.pointerenter || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointermove` on capturers
        if (capturers) {
            for (i = 0, n = capturers.length; i < n; i++) {
                capturer = capturers[i];
                handlers = listeners.pointermove || {};
                handler  = handlers[capturer.euid] || null;
                handler  && capturer[handler](pointer);

                if (capturer === target) {
                    targetIsCapturer = true;
                }
            }
        }

        // Fire `pointermove` on target if it isn't a capturer
        if (target && !targetIsCapturer) {
            handlers = listeners.pointermove || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerleave` if pointer just left a control
        if (previousTarget && target !== previousTarget) {
            handlers = listeners.pointerleave || {};
            handler  = handlers[previousTarget.euid] || null;
            handler  && previousTarget[handler](pointer);
        }
    },

    /**
     * Dispatch events to canvas controls when a pointer is up.
     *
     * @fires    enyo-canvas.EventDispatchSupport#pointerenter
     * @fires    enyo-canvas.EventDispatchSupport#pointerup
     * @fires    enyo-canvas.EventDispatchSupport#pointertap
     * @fires    enyo-canvas.EventDispatchSupport#pointerleave
     * @param    {enyo-canvas.PointerState}  pointer  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    dispatchPointerUpEvents: function(pointer) {
        var pointerId        = pointer.pointerId,
            previousTarget   = pointer.previousTarget,
            target           = pointer.target,
            capturers        = this.capturedPointers[pointerId] || [],
            targetIsCapturer = false,
            listeners        = this.eventListeners,
            handlers,
            handler,
            i,
            n,
            capturer;

        // Fire `pointerenter` if pointer just entered a new control
        if (target && target !== previousTarget) {
            handlers = listeners.pointerenter || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerup` on capturers
        if (capturers) {
            for (i = 0, n = capturers.length; i < n; i++) {
                capturer = capturers[i];
                handlers = listeners.pointerup || {};
                handler  = handlers[capturer.euid] || null;
                handler  && capturer[handler](pointer);

                if (capturer === target) {
                    targetIsCapturer = true;
                }
            }
        }

        // Fire `pointerup` on target if it isn't a capturer
        if (target && !targetIsCapturer) {
            handlers = listeners.pointerup || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointertap` on target.
        if (target && target === pointer.tapTarget && !pointer.isTapPrevented) {
            handlers = listeners.pointertap || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerleave` on previousTarget if it isn't the target anymore
        if (previousTarget && target !== previousTarget) {
            handlers = listeners.pointerleave || {};
            handler  = handlers[previousTarget.euid] || null;
            handler  && previousTarget[handler](pointer);
        }

        // TODO: Maybe remove old pointer
    },

    /**
     * Dispatch events to canvas controls when a pointer got canceled.
     *
     * @fires    enyo-canvas.EventDispatchSupport#pointercancel
     * @fires    enyo-canvas.EventDispatchSupport#pointerleave
     * @param    {enyo-canvas.PointerState}  pointer  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    dispatchPointerCancelEvents: function(pointer) {
        var pointerId        = pointer.pointerId,
            previousTarget   = pointer.previousTarget,
            target           = pointer.target,
            capturers        = this.capturedPointers[pointerId] || [],
            targetIsCapturer = false,
            listeners        = this.eventListeners,
            handlers,
            handler,
            i,
            n,
            capturer;

        // Fire `pointercancel` on capturers
        if (capturers) {
            for (i = 0, n = capturers.length; i < n; i++) {
                capturer = capturers[i];
                handlers = listeners.pointercancel || {};
                handler  = handlers[capturer.euid] || null;
                handler  && capturer[handler](pointer);

                if (capturer === target) {
                    targetIsCapturer = true;
                }
            }
        }

        // Fire `pointercancel` on target if it isn't a capturer
        if (target && !targetIsCapturer) {
            handlers = listeners.pointercancel || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerleave` on previousTarget if it isn't the target anymore
        if (previousTarget && target !== previousTarget) {
            handlers = listeners.pointerleave || {};
            handler  = handlers[previousTarget.euid] || null;
            handler  && previousTarget[handler](pointer);
        }

        delete this.pointers[pointerId];
    },

    /**
     * Executes event handlers as needed.
     *
     * @fires    enyo-canvas.EventDispatchSupport#wheel
     * @param    {enyo-canvas.WheelState}  wheel  The wheel
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    dispatchWheelEvent: function(wheel) {
        var pointerOnCanvas = wheel.srcEvent.target === this.hasNode(),
            listeners       = this.eventListeners,
            target,
            handlers,
            handler;

        // Nothing to do here
        if (!pointerOnCanvas) {
            return;
        }

        // Find target
        target = this.findPointerTarget(wheel);

        // Fire `wheel` on target
        if (target) {
            handlers = listeners.wheel || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](wheel);
        }
    },

    // ...........................
    // Events Utils

    /**
     * Generate a normalized event for a single pointer from a `PointerEvent`.
     *
     * @param    {PointerEvent}  event  The event
     * @returns  {Object}        The normalized event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    normalizePointerEvent: function(event) {
        var pointerEvent;

        pointerEvent = {
            pointerId:   event.pointerId,
            pointerType: event.pointerType,
            srcEvent:    event
        };
        pointerEvent = utils.mixin(pointerEvent, utils.only(this.eventProps, event));
        pointerEvent = utils.mixin(pointerEvent, utils.only(this.pointerProps, event));
        pointerEvent = utils.mixin(pointerEvent, this.calcCanvasCoordinates(event));

        return pointerEvent;
    },

    /**
     * Generate a normalized event for a single pointer from a `MouseEvent`.
     *
     * @param    {MouseEvent}  event  The event
     * @returns  {Object}      The normalized event
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    normalizeMouseEvent: function(event) {
        var pointerEvent;

        pointerEvent = {
            pointerId:   0,
            pointerType: "mouse",
            srcEvent:    event
        };
        pointerEvent = utils.mixin(pointerEvent, utils.only(this.eventProps, event));
        pointerEvent = utils.mixin(pointerEvent, utils.only(this.pointerProps, event));
        pointerEvent = utils.mixin(pointerEvent, this.calcCanvasCoordinates(event));

        return pointerEvent;
    },

    /**
     * Generate a normalized event for each touch in a `TouchEvent`.
     *
     * @param    {TouchEvent}     event  The event
     * @returns  {Array<Object>}  An array of normalized events
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    normalizeTouchEvent: function(event) {
        var touches,
            i,
            n,
            touch,
            pointerEvent,
            pointerEvents = [];

        event.preventDefault();
        touches = event.changedTouches;

        for (i = 0, n = touches.length; i < n; i++) {
            touch        = touches[i];
            pointerEvent = {
                pointerId:   touch.identifier,
                pointerType: "touch",
                srcEvent:    event,
                srcTouch:    touch
            };
            pointerEvent = utils.mixin(pointerEvent, utils.only(this.eventProps, event));
            pointerEvent = utils.mixin(pointerEvent, utils.only(this.pointerProps, touch));
            pointerEvent = utils.mixin(pointerEvent, this.calcCanvasCoordinates(touch));
            pointerEvents.push(pointerEvent);
        }

        return pointerEvents;
    },

    /**
     * Generate a normalized event for each touch in a `WheelEvent`.
     *
     * @param    {WheelEvent}              event  The event
     * @return   {enyo-canvas.WheelState}  wheel  The normalized wheel
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    normalizeWheelEvent: function(event) {
        var wheel = {
            srcEvent: event
        };

        wheel        = utils.mixin(wheel, utils.only(this.eventProps, event));
        wheel        = utils.mixin(wheel, utils.only(this.pointerProps, event));
        wheel        = utils.mixin(wheel, this.calcCanvasCoordinates(event));
        wheel.deltaX = event.deltaX || 0;
        wheel.deltaY = event.deltaY || 0;

        return wheel;
    },

    /**
     * Create a blank pointer state.
     *
     * @returns  {enyo-canvas.PointerState}  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    newPointerState: function() {
        return {
            target:         null,
            tapTarget:      null,
            isTapPrevented: false,

            /**
             * Prevent tap events
             * @memberof enyo-canvas.PointerState
             * @method
             * @public
             */
            preventTap: function() {
                this.isTapPrevented = true;
            }
        };
    },

    /**
     * Updates the pointer state
     *
     * @param    {Object}                    event  The pointer event
     * @returns  {enyo-canvas.PointerState}  The pointer state
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    updatePointerState: function(event) {
        var pointers  = this.pointers,
            pointerId = event.pointerId,
            pointer   = pointers[pointerId],
            pointerOnCanvas,
            prevClientX,
            prevClientY,
            prevDx,
            prevDy,
            target;

        // Prerequisites for calculating movement
        prevClientX = pointer ? pointer.clientX : event.clientX;
        prevClientY = pointer ? pointer.clientY : event.clientY;
        prevDx      = pointer ? pointer.dx : 0;
        prevDy      = pointer ? pointer.dy : 0;

        // Ensure a pointer state exists
        if (!pointer) {
            pointer = pointers[pointerId] = this.newPointerState(event);
        }

        // Update pointer
        pointer = utils.mixin(pointer, event);

        // Calculate pointer movement
        switch (event.srcEvent.type) {
            case "pointerdown":
            case "mousedown":
            case "touchstart":
                pointer.dx  = 0;
                pointer.dy  = 0;
                pointer.ddx = 0;
                pointer.ddy = 0;
                break;
            default:
                pointer.dx  = event.clientX - prevClientX;
                pointer.dy  = event.clientY - prevClientY;
                pointer.ddx = pointer.dx - prevDx;
                pointer.ddy = pointer.dy - prevDy;
                break;
        }

        // Calculate pointer targeting
        pointerOnCanvas         = event.srcEvent.target === this.hasNode();
        pointer.pointerOnCanvas = pointerOnCanvas;
        pointer.previousTarget  = pointer.target;
        pointer.target          = target = pointerOnCanvas ? this.findPointerTarget(event) : null;

        switch (event.srcEvent.type) {
            case "pointerdown":
            case "mousedown":
            case "touchstart":
                pointer.tapTarget      = target;
                pointer.isTapPrevented = false;
                break;
            default:
                break;
        }

        return pointer;
    },

    /**
     * Calculates the coordinates relative to the top-left corner of the canvas
     * in css pixels.
     *
     * @param    {Event}   event  The event
     * @return   {Object}  The coordinates, stored at `canvasX` and `canvasY`.
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @public
     */
    calcCanvasCoordinates: function(event) {
        var bounds = this.boundingClientRect;

        return {
            canvasX: event.clientX - bounds.left,
            canvasY: event.clientY - bounds.top
        };
    },

    /**
     * Find out if this event is targeted at a control and return it if
     * found.
     *
     * @param    {Object}    event  The event
     * @returns  {?Control}  The control if found, null otherwise
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    findPointerTarget: function(event) {
        var i,
            controls = this.controls,
            control;

        // We iterate backwards over controls because the last control to be
        // drawn will obscure any controls below.
        for (i = controls.length - 1; i >= 0; i--) {
            control = controls[i];

            if (control.cssHitTest && control.cssHitTest(event.canvasX, event.canvasY)) {
                return control;
            }
        }

        return null;
    },

    // ...........................
    // Scene State

    /**
     * After all children have updated, do a new round of hit-tests for each
     * pointer that's currently on the canvas and generate `pointerenter` and
     * `pointerleave` events as needed.
     *
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @protected
     */
    update: kind.inherit(function(sup) {
        return function() {
            var pointers = this.pointers,
                pointerId,
                pointer;

            sup.apply(this, arguments);

            for (pointerId in pointers) {
                if (pointers.hasOwnProperty(pointerId)) {
                    pointer                 = pointers[pointerId];
                    pointer.pointerOnCanvas && this.updatePointerTarget(pointer);
                }
            }
        };
    }),

    /**
     * Updates the target for the provided pointer and fires `pointerenter`
     * and `pointerleave` events as needed.
     *
     * @param  {Object}  pointer  The pointer
     * @memberof enyo-canvas.EventDispatchSupport
     * @method
     * @private
     */
    updatePointerTarget: function(pointer) {
        var listeners      = this.eventListeners,
            previousTarget,
            target,
            handlers,
            handler;

        // Update pointer target
        previousTarget = pointer.previousTarget = pointer.target;
        target         = pointer.target = this.findPointerTarget(pointer);

        // Fire `pointerenter` if pointer just entered a new control
        if (target && target !== previousTarget) {
            handlers = listeners.pointerenter || {};
            handler  = handlers[target.euid] || null;
            handler  && target[handler](pointer);
        }

        // Fire `pointerleave` if pointer just left a control
        if (previousTarget && target !== previousTarget) {
            handlers = listeners.pointerleave || {};
            handler  = handlers[previousTarget.euid] || null;
            handler  && previousTarget[handler](pointer);
        }
    }

};
